import React, { useState, useEffect } from 'react';
import API from '../services/api';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';

// Color palette for session types
const SESSION_TYPE_COLORS = {
  'Individual': '#3498db',
  'Family': '#9b59b6',
  'Group': '#e67e22',
  'Couples': '#e74c3c',
  'Child': '#1abc9c',
  'Adolescent': '#f39c12',
  'Assessment': '#34495e',
  'Consultation': '#16a085',
  'Unknown': '#95a5a6',
  'default': '#7f8c8d'
};

const getTypeColor = (type) => {
  return SESSION_TYPE_COLORS[type] || SESSION_TYPE_COLORS['default'];
};

const SessionSummaryCard = () => {
  const [summaryData, setSummaryData] = useState({
    today: { count: 0, avgRate: '0.00', byType: [] },
    week: { count: 0, avgRate: '0.00', byType: [] },
    month: { count: 0, avgRate: '0.00', byType: [] }
  });
  const [loading, setLoading] = useState(true);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [periodType, setPeriodType] = useState('day');
  const [viewMode, setViewMode] = useState('table'); // 'table', 'chart', 'byType'
  const [therapists, setTherapists] = useState([]);
  const [selectedTherapist, setSelectedTherapist] = useState(null);
  const [expandedPeriod, setExpandedPeriod] = useState(null); // For showing type breakdown in card
  
  useEffect(() => {
    fetchTherapists();
    fetchSummaryData();
  }, []);
  
  useEffect(() => {
    fetchSummaryData(selectedTherapist);
  }, [selectedTherapist]);

  const fetchTherapists = async () => {
    try {
      const res = await API.get('/admin/active-therapists');
      setTherapists(res.data);
    } catch (err) {
      console.error('Error fetching therapists:', err);
    }
  };

  const fetchSummaryData = async (therapistId = null) => {
    try {
      setLoading(true);
      let url = '/admin/session-summary';
      if (therapistId) {
        url += `?therapist_id=${therapistId}`;
      }
      const res = await API.get(url);
      setSummaryData(res.data);
    } catch (err) {
      console.error('Error fetching session summary:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistoryData = async (type, therapistId = selectedTherapist) => {
    try {
      setHistoryLoading(true);
      setPeriodType(type);
      let url = `/admin/session-history?type=${type}`;
      if (therapistId) {
        url += `&therapist_id=${therapistId}`;
      }
      const res = await API.get(url);
      setHistoryData(res.data);
      setShowHistoryModal(true);
    } catch (err) {
      console.error(`Error fetching ${type} history:`, err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleCardClick = () => {
    fetchHistoryData('day');
  };

  const handleTherapistChange = (e) => {
    const therapistId = e.target.value === 'all' ? null : e.target.value;
    setSelectedTherapist(therapistId);
    if (showHistoryModal) {
      fetchHistoryData(periodType, therapistId);
    }
  };

  const getPeriodLabel = (period) => {
    switch (periodType) {
      case 'day':
        return new Date(period).toLocaleDateString();
      case 'week':
        const weekStart = new Date(period.startDate);
        const weekEnd = new Date(period.endDate);
        return `${weekStart.toLocaleDateString()} - ${weekEnd.toLocaleDateString()}`;
      case 'month':
        const date = new Date(period);
        return date.toLocaleDateString('default', { month: 'long', year: 'numeric' });
      default:
        return period;
    }
  };

  const getShortPeriodLabel = (period) => {
    switch (periodType) {
      case 'day':
        return new Date(period).toLocaleDateString('default', { month: 'short', day: 'numeric' });
      case 'week':
        return new Date(period.startDate).toLocaleDateString('default', { month: 'short', day: 'numeric' });
      case 'month':
        return new Date(period).toLocaleDateString('default', { month: 'short', year: '2-digit' });
      default:
        return period;
    }
  };

  // Prepare data for bar chart (sessions count)
  const prepareChartData = () => {
    return historyData.map(item => ({
      name: getShortPeriodLabel(item.period),
      sessions: item.count,
      avgRate: parseFloat(item.avg_rate) || 0
    })).reverse();
  };

  // Prepare data for session type breakdown chart
  const prepareTypeChartData = () => {
    const typeMap = {};
    
    historyData.forEach(item => {
      if (item.by_type && Array.isArray(item.by_type)) {
        item.by_type.forEach(typeData => {
          if (typeData.type) {
            if (!typeMap[typeData.type]) {
              typeMap[typeData.type] = {
                type: typeData.type,
                count: 0,
                totalBilled: 0
              };
            }
            const count = typeData.count || 0;
            const avgRate = parseFloat(typeData.avgRate) || 0;
            typeMap[typeData.type].count += count;
            // Accumulate total billed (avgRate * count gives us the total for that period)
            typeMap[typeData.type].totalBilled += avgRate * count;
          }
        });
      }
    });
    
    return Object.values(typeMap)
      .map(item => ({
        type: item.type,
        // Truncate long type names for chart display
        shortType: item.type.length > 20 ? item.type.substring(0, 18) + '...' : item.type,
        count: item.count,
        avgRate: item.count > 0 ? parseFloat((item.totalBilled / item.count).toFixed(2)) : 0
      }))
      .sort((a, b) => b.count - a.count);
  };

  const getTherapistName = () => {
    if (!selectedTherapist) return 'All Therapists';
    const therapist = therapists.find(t => t.id.toString() === selectedTherapist.toString());
    return therapist ? `${therapist.first_name} ${therapist.last_name}` : 'Selected Therapist';
  };

  const formatCurrency = (value) => {
    return `$${parseFloat(value).toFixed(2)}`;
  };

  const togglePeriodExpand = (period, e) => {
    e.stopPropagation();
    setExpandedPeriod(expandedPeriod === period ? null : period);
  };

  const renderTypeBreakdown = (byType) => {
    if (!byType || byType.length === 0) {
      return <div style={noDataStyle}>No session data</div>;
    }
    
    return (
      <div style={typeBreakdownStyle}>
        {byType.map((item, idx) => (
          <div key={idx} style={typeItemStyle}>
            <span style={{...typeBadgeStyle, backgroundColor: getTypeColor(item.type)}}>
              {item.type}
            </span>
            <span style={typeCountStyle}>{item.count} sessions</span>
            <span style={typeRateStyle}>{formatCurrency(item.avgRate)} avg</span>
          </div>
        ))}
      </div>
    );
  };

  // Custom tooltip for bar chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={tooltipStyle}>
          <p style={tooltipLabelStyle}>{label}</p>
          <p style={tooltipValueStyle}>Sessions: {payload[0]?.value}</p>
          {payload[1] && (
            <p style={tooltipValueStyle}>Avg Rate: {formatCurrency(payload[1]?.value)}</p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <div style={cardStyle}>
        <div style={cardHeaderStyle}>
          <h3 style={cardTitleStyle}>Session Summary</h3>
          
          <select 
            style={selectStyle} 
            value={selectedTherapist || 'all'} 
            onChange={handleTherapistChange}
            onClick={(e) => e.stopPropagation()}
          >
            <option value="all">All Therapists</option>
            {therapists.map(therapist => (
              <option key={therapist.id} value={therapist.id}>
                {therapist.first_name} {therapist.last_name}
              </option>
            ))}
          </select>
        </div>
        
        {loading ? (
          <div style={loadingStyle}>Loading summary data...</div>
        ) : (
          <div style={statsContainerStyle} onClick={handleCardClick}>
            {/* Today */}
            <div style={statItemStyle}>
              <span style={statLabelStyle}>Today</span>
              <span style={statValueStyle}>{summaryData.today?.count || 0}</span>
              <span style={avgRateStyle}>{formatCurrency(summaryData.today?.avgRate || 0)} avg</span>
              {summaryData.today?.byType?.length > 0 && (
                <button 
                  style={expandButtonStyle}
                  onClick={(e) => togglePeriodExpand('today', e)}
                >
                  {expandedPeriod === 'today' ? '▲' : '▼'} By Type
                </button>
              )}
              {expandedPeriod === 'today' && renderTypeBreakdown(summaryData.today?.byType)}
            </div>
            
            {/* This Week */}
            <div style={statItemStyle}>
              <span style={statLabelStyle}>This Week</span>
              <span style={statValueStyle}>{summaryData.week?.count || 0}</span>
              <span style={avgRateStyle}>{formatCurrency(summaryData.week?.avgRate || 0)} avg</span>
              {summaryData.week?.byType?.length > 0 && (
                <button 
                  style={expandButtonStyle}
                  onClick={(e) => togglePeriodExpand('week', e)}
                >
                  {expandedPeriod === 'week' ? '▲' : '▼'} By Type
                </button>
              )}
              {expandedPeriod === 'week' && renderTypeBreakdown(summaryData.week?.byType)}
            </div>
            
            {/* This Month */}
            <div style={statItemStyle}>
              <span style={statLabelStyle}>This Month</span>
              <span style={statValueStyle}>{summaryData.month?.count || 0}</span>
              <span style={avgRateStyle}>{formatCurrency(summaryData.month?.avgRate || 0)} avg</span>
              {summaryData.month?.byType?.length > 0 && (
                <button 
                  style={expandButtonStyle}
                  onClick={(e) => togglePeriodExpand('month', e)}
                >
                  {expandedPeriod === 'month' ? '▲' : '▼'} By Type
                </button>
              )}
              {expandedPeriod === 'month' && renderTypeBreakdown(summaryData.month?.byType)}
            </div>
          </div>
        )}
        <div style={clickInstructionStyle}>Click for historical data</div>
      </div>

      {/* History Modal */}
      {showHistoryModal && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <div style={modalHeaderStyle}>
              <h2 style={modalTitleStyle}>
                Session History - {getTherapistName()}
              </h2>
              <div style={modalActionsStyle}>
                <button 
                  onClick={() => setShowHistoryModal(false)}
                  style={modalCloseButtonStyle}
                >
                  &times;
                </button>
              </div>
            </div>

            <div style={modalBodyStyle}>
              {/* Period Tabs */}
              <div style={periodTabsStyle}>
                <button 
                  style={periodType === 'day' ? activeTabStyle : tabStyle}
                  onClick={() => fetchHistoryData('day')}
                >
                  Daily
                </button>
                <button 
                  style={periodType === 'week' ? activeTabStyle : tabStyle}
                  onClick={() => fetchHistoryData('week')}
                >
                  Weekly
                </button>
                <button 
                  style={periodType === 'month' ? activeTabStyle : tabStyle}
                  onClick={() => fetchHistoryData('month')}
                >
                  Monthly
                </button>
              </div>

              {/* View Mode Tabs */}
              <div style={viewModeContainerStyle}>
                <div style={viewModeTabsStyle}>
                  <button 
                    style={viewMode === 'table' ? activeViewTabStyle : viewTabStyle}
                    onClick={() => setViewMode('table')}
                  >
                    📋 Table
                  </button>
                  <button 
                    style={viewMode === 'chart' ? activeViewTabStyle : viewTabStyle}
                    onClick={() => setViewMode('chart')}
                  >
                    📊 Chart
                  </button>
                  <button 
                    style={viewMode === 'byType' ? activeViewTabStyle : viewTabStyle}
                    onClick={() => setViewMode('byType')}
                  >
                    📈 By Type
                  </button>
                </div>
                
                <select 
                  style={selectStyle} 
                  value={selectedTherapist || 'all'} 
                  onChange={handleTherapistChange}
                >
                  <option value="all">All Therapists</option>
                  {therapists.map(therapist => (
                    <option key={therapist.id} value={therapist.id}>
                      {therapist.first_name} {therapist.last_name}
                    </option>
                  ))}
                </select>
              </div>

              {historyLoading ? (
                <div style={loadingStyle}>Loading history data...</div>
              ) : (
                <>
                  {/* Table View */}
                  {viewMode === 'table' && (
                    <div style={historyContainerStyle}>
                      <table style={tableStyle}>
                        <thead>
                          <tr>
                            <th style={thStyle}>Period</th>
                            <th style={thStyle}>Sessions</th>
                            <th style={thStyle}>Avg Rate</th>
                            <th style={thStyle}>By Type</th>
                          </tr>
                        </thead>
                        <tbody>
                          {historyData.map((item, index) => (
                            <tr key={index} style={index % 2 === 0 ? {} : trAlternateStyle}>
                              <td style={tdStyle}>{getPeriodLabel(item.period)}</td>
                              <td style={{...tdStyle, textAlign: 'center', fontWeight: 'bold'}}>
                                {item.count}
                              </td>
                              <td style={{...tdStyle, textAlign: 'center', color: '#27ae60'}}>
                                {formatCurrency(item.avg_rate || 0)}
                              </td>
                              <td style={tdStyle}>
                                {item.by_type && item.by_type.length > 0 ? (
                                  <div style={typeListStyle}>
                                    {item.by_type.slice(0, 3).map((t, i) => (
                                      <span 
                                        key={i} 
                                        style={{...miniTypeBadgeStyle, backgroundColor: getTypeColor(t.type)}}
                                        title={`${t.count} sessions @ ${formatCurrency(t.avgRate)} avg`}
                                      >
                                        {t.type}: {t.count}
                                      </span>
                                    ))}
                                    {item.by_type.length > 3 && (
                                      <span style={moreTypesStyle}>+{item.by_type.length - 3} more</span>
                                    )}
                                  </div>
                                ) : (
                                  <span style={noDataStyle}>—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                          {historyData.length === 0 && (
                            <tr>
                              <td colSpan="4" style={{...tdStyle, textAlign: 'center'}}>
                                No data available
                              </td>
                            </tr>
                          )}
                        </tbody>
                        {historyData.length > 0 && (
                          <tfoot>
                            <tr style={{fontWeight: 'bold', backgroundColor: '#f0f8ff'}}>
                              <td style={tdStyle}>Total</td>
                              <td style={{...tdStyle, textAlign: 'center'}}>
                                {historyData.reduce((sum, item) => sum + item.count, 0)}
                              </td>
                              <td style={{...tdStyle, textAlign: 'center', color: '#27ae60'}}>
                                {formatCurrency(
                                  historyData.reduce((sum, item) => sum + item.count, 0) > 0
                                    ? historyData.reduce((sum, item) => sum + (parseFloat(item.avg_rate) || 0) * item.count, 0) / 
                                      historyData.reduce((sum, item) => sum + item.count, 0)
                                    : 0
                                )}
                              </td>
                              <td style={tdStyle}>—</td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  )}

                  {/* Bar Chart View */}
                  {viewMode === 'chart' && (
                    <div style={graphContainerStyle}>
                      {historyData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={350}>
                          <BarChart
                            data={prepareChartData()}
                            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="name" 
                              tick={{ fontSize: 11 }} 
                              interval={periodType === 'day' ? 2 : 0}
                              angle={periodType === 'day' ? -45 : 0}
                              textAnchor={periodType === 'day' ? 'end' : 'middle'}
                              height={periodType === 'day' ? 60 : 30}
                            />
                            <YAxis yAxisId="left" orientation="left" stroke="#3498db" />
                            <YAxis yAxisId="right" orientation="right" stroke="#27ae60" />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                            <Bar 
                              yAxisId="left"
                              dataKey="sessions" 
                              fill="#3498db" 
                              name="Sessions"
                              radius={[4, 4, 0, 0]}
                            />
                            <Bar 
                              yAxisId="right"
                              dataKey="avgRate" 
                              fill="#27ae60" 
                              name="Avg Rate ($)"
                              radius={[4, 4, 0, 0]}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div style={{textAlign: 'center', padding: '2rem'}}>No data available for chart</div>
                      )}
                    </div>
                  )}

                  {/* By Type View */}
                  {viewMode === 'byType' && (
                    <div style={byTypeContainerStyle}>
                      {prepareTypeChartData().length > 0 ? (
                        <>
                          <div style={{...typeChartContainerStyle, height: Math.max(300, prepareTypeChartData().length * 50)}}>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart
                                data={prepareTypeChartData()}
                                layout="vertical"
                                margin={{ top: 20, right: 80, left: 20, bottom: 5 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" />
                                <YAxis 
                                  dataKey="shortType" 
                                  type="category" 
                                  tick={{ fontSize: 11 }}
                                  width={150}
                                  interval={0}
                                />
                                <Tooltip 
                                  formatter={(value, name) => [
                                    name === 'Sessions' ? value : formatCurrency(value),
                                    name
                                  ]}
                                  labelFormatter={(label) => {
                                    const item = prepareTypeChartData().find(d => d.shortType === label);
                                    return item ? item.type : label;
                                  }}
                                />
                                <Legend />
                                <Bar 
                                  dataKey="count" 
                                  name="Sessions" 
                                  fill="#3498db"
                                  radius={[0, 4, 4, 0]}
                                >
                                  {prepareTypeChartData().map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={getTypeColor(entry.type)} />
                                  ))}
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                          
                          <div style={typeSummaryTableStyle}>
                            <h4 style={{marginTop: 0, marginBottom: '1rem'}}>Session Type Summary</h4>
                            <table style={tableStyle}>
                              <thead>
                                <tr>
                                  <th style={thStyle}>Type</th>
                                  <th style={thStyle}>Sessions</th>
                                  <th style={thStyle}>Avg Rate</th>
                                </tr>
                              </thead>
                              <tbody>
                                {prepareTypeChartData().map((item, index) => (
                                  <tr key={index} style={index % 2 === 0 ? {} : trAlternateStyle}>
                                    <td style={tdStyle}>
                                      <span style={{...typeBadgeStyle, backgroundColor: getTypeColor(item.type)}}>
                                        {item.type}
                                      </span>
                                    </td>
                                    <td style={{...tdStyle, textAlign: 'center', fontWeight: 'bold'}}>
                                      {item.count}
                                    </td>
                                    <td style={{...tdStyle, textAlign: 'center', color: '#27ae60'}}>
                                      {formatCurrency(item.avgRate)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr style={{fontWeight: 'bold', backgroundColor: '#f0f8ff'}}>
                                  <td style={tdStyle}>Total</td>
                                  <td style={{...tdStyle, textAlign: 'center'}}>
                                    {prepareTypeChartData().reduce((sum, item) => sum + item.count, 0)}
                                  </td>
                                  <td style={{...tdStyle, textAlign: 'center', color: '#27ae60'}}>
                                    {formatCurrency(
                                      prepareTypeChartData().reduce((sum, item) => sum + item.count, 0) > 0
                                        ? prepareTypeChartData().reduce((sum, item) => sum + item.avgRate * item.count, 0) / 
                                          prepareTypeChartData().reduce((sum, item) => sum + item.count, 0)
                                        : 0
                                    )}
                                  </td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </>
                      ) : (
                        <div style={{textAlign: 'center', padding: '2rem'}}>No session type data available</div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Styles
const cardStyle = {
  flex: '1 1 400px',
  minWidth: '300px',
  maxWidth: '550px',
  padding: '1.5rem',
  backgroundColor: '#f0f8ff',
  borderRadius: '8px',
  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  cursor: 'pointer'
};

const cardHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '1rem'
};

const cardTitleStyle = {
  margin: 0,
  fontSize: '1.3rem',
  color: '#2c3e50'
};

const selectStyle = {
  padding: '0.4rem 0.6rem',
  borderRadius: '4px',
  border: '1px solid #ddd',
  fontSize: '0.85rem',
  color: '#2c3e50',
  backgroundColor: 'white',
  cursor: 'pointer'
};

const statsContainerStyle = {
  display: 'flex',
  justifyContent: 'space-around',
  marginBottom: '1rem',
  gap: '0.5rem'
};

const statItemStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  flex: 1,
  padding: '0.5rem',
  borderRadius: '6px',
  backgroundColor: 'rgba(255,255,255,0.5)'
};

const statLabelStyle = {
  fontSize: '0.9rem',
  color: '#7f8c8d',
  marginBottom: '0.3rem'
};

const statValueStyle = {
  fontSize: '2rem',
  fontWeight: 'bold',
  color: '#3498db'
};

const avgRateStyle = {
  fontSize: '0.85rem',
  color: '#27ae60',
  fontWeight: '600',
  marginTop: '0.2rem'
};

const expandButtonStyle = {
  marginTop: '0.5rem',
  padding: '0.2rem 0.5rem',
  fontSize: '0.75rem',
  backgroundColor: 'transparent',
  border: '1px solid #bdc3c7',
  borderRadius: '4px',
  cursor: 'pointer',
  color: '#7f8c8d'
};

const typeBreakdownStyle = {
  marginTop: '0.5rem',
  padding: '0.5rem',
  backgroundColor: 'white',
  borderRadius: '4px',
  width: '100%'
};

const typeItemStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0.3rem 0',
  fontSize: '0.75rem',
  borderBottom: '1px solid #ecf0f1'
};

const typeBadgeStyle = {
  padding: '0.2rem 0.5rem',
  borderRadius: '4px',
  color: 'white',
  fontSize: '0.75rem',
  fontWeight: '500'
};

const typeCountStyle = {
  color: '#2c3e50',
  fontWeight: '500'
};

const typeRateStyle = {
  color: '#27ae60',
  fontWeight: '600'
};

const loadingStyle = {
  textAlign: 'center',
  padding: '1rem',
  color: '#7f8c8d'
};

const clickInstructionStyle = {
  textAlign: 'center',
  fontSize: '0.8rem',
  color: '#95a5a6',
  marginTop: '0.5rem'
};

const noDataStyle = {
  color: '#95a5a6',
  fontStyle: 'italic',
  fontSize: '0.8rem'
};

// Modal Styles
const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000
};

const modalContentStyle = {
  backgroundColor: 'white',
  borderRadius: '8px',
  width: '95%',
  maxWidth: '1000px',
  maxHeight: '90vh',
  overflow: 'auto',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)'
};

const modalHeaderStyle = {
  padding: '1.5rem',
  borderBottom: '1px solid #e0e0e0',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
};

const modalTitleStyle = {
  margin: 0,
  fontSize: '1.5rem'
};

const modalActionsStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '1rem'
};

const modalCloseButtonStyle = {
  background: 'none',
  border: 'none',
  fontSize: '1.5rem',
  cursor: 'pointer',
  color: '#666'
};

const modalBodyStyle = {
  padding: '1.5rem'
};

const periodTabsStyle = {
  display: 'flex',
  justifyContent: 'center',
  marginBottom: '1rem',
  borderBottom: '1px solid #e0e0e0',
  paddingBottom: '0.5rem'
};

const tabStyle = {
  padding: '0.5rem 1rem',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  color: '#7f8c8d',
  fontSize: '1rem',
  margin: '0 0.5rem',
  borderRadius: '4px'
};

const activeTabStyle = {
  ...tabStyle,
  backgroundColor: '#3498db',
  color: 'white',
  fontWeight: 'bold'
};

const viewModeContainerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '1rem',
  flexWrap: 'wrap',
  gap: '0.5rem'
};

const viewModeTabsStyle = {
  display: 'flex',
  gap: '0.5rem'
};

const viewTabStyle = {
  padding: '0.4rem 0.8rem',
  border: '1px solid #ddd',
  background: 'white',
  cursor: 'pointer',
  color: '#7f8c8d',
  fontSize: '0.9rem',
  borderRadius: '4px'
};

const activeViewTabStyle = {
  ...viewTabStyle,
  backgroundColor: '#ecf0f1',
  color: '#2c3e50',
  fontWeight: '600',
  borderColor: '#bdc3c7'
};

const historyContainerStyle = {
  maxHeight: '400px',
  overflow: 'auto'
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse'
};

const thStyle = {
  backgroundColor: '#f5f5f5',
  padding: '0.75rem',
  textAlign: 'left',
  borderBottom: '2px solid #ddd',
  position: 'sticky',
  top: 0
};

const tdStyle = {
  padding: '0.75rem',
  borderBottom: '1px solid #eee'
};

const trAlternateStyle = {
  backgroundColor: '#f9f9f9'
};

const typeListStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.3rem'
};

const miniTypeBadgeStyle = {
  padding: '0.15rem 0.4rem',
  borderRadius: '3px',
  color: 'white',
  fontSize: '0.7rem',
  fontWeight: '500'
};

const moreTypesStyle = {
  fontSize: '0.7rem',
  color: '#7f8c8d',
  fontStyle: 'italic'
};

const graphContainerStyle = {
  width: '100%',
  height: '400px',
  marginTop: '1rem'
};

const byTypeContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1.5rem'
};

const typeChartContainerStyle = {
  width: '100%',
  height: '320px'
};

const typeSummaryTableStyle = {
  marginTop: '1rem'
};

const tooltipStyle = {
  backgroundColor: 'white',
  padding: '0.75rem',
  border: '1px solid #ddd',
  borderRadius: '4px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
};

const tooltipLabelStyle = {
  margin: 0,
  fontWeight: 'bold',
  marginBottom: '0.5rem'
};

const tooltipValueStyle = {
  margin: 0,
  fontSize: '0.9rem'
};

export default SessionSummaryCard;
